import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { User, KeyRound } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/";
  const expired = params.get("expired") === "1";
  // `?tab=staff` deep-links directly to the barman/server card (used from
  // the header dropdown so staff never see a customer-first login by
  // accident).
  const initialTab = params.get("tab") === "staff" ? "staff" : "customer";
  const [tab, setTab] = useState(initialTab);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await login(email, password);
      toast.success("Bienvenue !");
      nav(next);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-20" data-testid="login-page">
      <h1 className="font-display text-4xl mb-8 text-center">Connexion</h1>
      {expired && (
        <div className="mb-5 rounded-lg border border-amber-400/30 bg-amber-400/5 px-4 py-3 text-sm text-amber-200" data-testid="session-expired-notice">
          Votre session a expiré, veuillez vous reconnecter.
        </div>
      )}

      {/* Tab switcher: customer email/password vs. staff PIN gateway */}
      <div className="mb-6 grid grid-cols-2 gap-2 p-1 rounded-full border border-border bg-card/50" data-testid="login-tab-switcher">
        <button
          type="button"
          onClick={() => setTab("customer")}
          className={`h-10 rounded-full text-sm font-medium flex items-center justify-center gap-2 transition ${
            tab === "customer"
              ? "bg-primary text-primary-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
          data-testid="login-tab-customer"
        >
          <User className="w-4 h-4" /> Client
        </button>
        <button
          type="button"
          onClick={() => setTab("staff")}
          className={`h-10 rounded-full text-sm font-medium flex items-center justify-center gap-2 transition ${
            tab === "staff"
              ? "bg-primary text-primary-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
          data-testid="login-tab-staff"
        >
          <KeyRound className="w-4 h-4" /> Barman / Serveur
        </button>
      </div>

      {tab === "customer" ? (
        <form onSubmit={submit} className="space-y-5 rounded-xl border border-border bg-card p-8" data-testid="login-form">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="bg-input mt-2" data-testid="login-email" />
          </div>
          <div>
            <Label htmlFor="password">Mot de passe</Label>
            <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="bg-input mt-2" data-testid="login-password" />
          </div>
          {error && <div className="text-sm text-destructive" data-testid="login-error">{error}</div>}
          <Button type="submit" disabled={submitting} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full h-12" data-testid="login-submit-btn">
            {submitting ? "Connexion…" : "Se connecter"}
          </Button>
          <div className="text-sm text-center text-muted-foreground">
            Pas encore de compte ?{" "}
            <Link to={next !== "/" ? `/inscription?next=${encodeURIComponent(next)}` : "/inscription"} className="gold-text hover:underline" data-testid="link-register">Créer un compte</Link>
          </div>
        </form>
      ) : (
        // Staff / POS entry — no email + password here; the PIN gate at
        // /pos is the source of truth for barman & servers.
        <div className="rounded-xl border border-border bg-card p-8 text-center space-y-5" data-testid="login-staff-card">
          <div className="mx-auto w-16 h-16 rounded-full bg-primary/15 border border-primary/40 flex items-center justify-center">
            <KeyRound className="w-7 h-7 text-primary" />
          </div>
          <div>
            <h2 className="font-display text-2xl mb-2">Accès Barman / Serveur</h2>
            <p className="text-sm text-muted-foreground">
              Ouvrez le point de vente tablette et saisissez votre PIN à 4 chiffres.<br />
              Pas encore de PIN&nbsp;? Utilisez « Créer mon accès » sur l'écran suivant.
            </p>
          </div>
          <Button
            onClick={() => nav("/pos")}
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full h-12"
            data-testid="login-staff-goto-pos-btn"
          >
            Ouvrir le POS →
          </Button>
          <div className="text-xs text-muted-foreground pt-2 border-t border-border">
            Sessions PIN valables 12&nbsp;h · L'accès reste strictement réservé aux comptes staff approuvés.
          </div>
        </div>
      )}
    </div>
  );
}
